
import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { makeAvatarsBucketPublic } from "@/integrations/supabase/utils";

export type TeamMember = {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  role: string;
  phone?: string | null;
  avatar_url?: string | null;
  user_metadata?: Record<string, any>;
};

interface AuthContextType {
  isLoading: boolean;
  isAuthenticated: boolean;
  userName: string;
  userRole: string;
  user: TeamMember | null;
  session?: any;
  signIn: (email: string, password: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  forceSignOut: () => void;
}

// Create context with initial undefined value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Initialize with stable default values - non-blocking approach
  const [authState, setAuthState] = useState({
    isLoading: true, // Start as loading to prevent flashing of unauthorized content
    isAuthenticated: false,
    userName: "User",
    userRole: "viewer",
    user: null as TeamMember | null,
    sessionToken: null as string | null,
    initialized: false // Track if we've attempted to initialize auth
  });
  const { toast } = useToast();
  
  // Session health check and auto-refresh logic
  const checkSessionHealth = async (token: string): Promise<boolean> => {
    try {
      console.log("Auth: Performing session health check");
      const user = await getUserByToken(token);
      return user !== null;
    } catch (error) {
      console.error("Auth: Session health check failed:", error);
      return false;
    }
  };

  // Auto-refresh token before expiry (tokens expire after 7 days, refresh after 6 days)
  const scheduleTokenRefresh = (token: string) => {
    // Clear any existing refresh timeout
    if (window.authRefreshTimeout) {
      clearTimeout(window.authRefreshTimeout);
    }
    
    // Schedule refresh for 6 days (518400000 ms)
    window.authRefreshTimeout = setTimeout(async () => {
      console.log("Auth: Auto-refreshing session token");
      const isHealthy = await checkSessionHealth(token);
      if (!isHealthy) {
        console.log("Auth: Token expired, forcing logout");
        handleExpiredToken();
      }
    }, 518400000); // 6 days in milliseconds
  };

  // Handle expired or invalid tokens
  const handleExpiredToken = () => {
    console.log("Auth: Handling expired token - clearing session");
    localStorage.removeItem('session_token');
    localStorage.removeItem('auth_user');
    
    if (window.authRefreshTimeout) {
      clearTimeout(window.authRefreshTimeout);
    }
    
    setAuthState({
      isLoading: false,
      isAuthenticated: false,
      userName: "User",
      userRole: "viewer",
      user: null,
      sessionToken: null,
      initialized: true
    });

    toast({
      variant: "destructive",
      title: "Session Expired",
      description: "Your session has expired. Please log in again.",
    });
  };

  // Enhanced sign in with better error handling and debugging
  const signIn = async (email: string, password: string): Promise<boolean> => {
    try {
      console.log("Auth: Attempting to authenticate user", email);
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      // First, let's test if we can connect to Supabase at all
      console.log("Auth: Testing Supabase connection...");
      
      // Try a simple query to test connectivity
      const { data: testData, error: testError } = await supabase
        .from('team_members')
        .select('count')
        .limit(1);
      
      if (testError) {
        console.error("Auth: Supabase connection test failed:", testError);
        throw new Error(`Database connection failed: ${testError.message}`);
      }
      
      console.log("Auth: Supabase connection successful");
      
      // Now try the authentication function
      console.log("Auth: Calling authenticate_team_member function...");
      const { data, error } = await supabase.rpc('authenticate_team_member', {
        p_email: email,
        p_password: password
      });
      
      if (error) {
        console.error("Auth: RPC error during authentication:", error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
        
        // Enhanced error handling for different scenarios
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          throw new Error("Authentication function not found. Please contact support - the database may need to be set up.");
        } else if (error.message?.includes('connection') || error.message?.includes('network')) {
          throw new Error("Network error. Please check your connection and try again.");
        } else if (error.code === 'PGRST116') {
          throw new Error("Authentication function not available. Please contact support.");
        } else {
          throw new Error(`Authentication error: ${error.message}`);
        }
      }
      
      console.log("Auth: Authentication response data:", data);
      
      if (data && data.length > 0) {
        const authResult = data[0];
        if (authResult.success && authResult.session_token) {
          console.log("Auth: Authentication successful, storing session token");
          console.log("Auth: Team member ID:", authResult.team_member_id);
          
          // Store the new token
          localStorage.setItem('session_token', authResult.session_token);
          
          // Load user data with the new token
          const user = await getUserByToken(authResult.session_token);
          
          if (user) {
            const displayName = user.first_name && user.last_name
              ? `${user.first_name} ${user.last_name}`
              : user.email.split('@')[0];
            
            console.log("Auth: User authenticated successfully", { 
              id: user.id, 
              displayName, 
              email: user.email,
              role: user.role 
            });
            
            // Store user info in localStorage for consistency
            localStorage.setItem('auth_user', JSON.stringify({
              id: user.id,
              email: user.email,
              first_name: user.first_name,
              last_name: user.last_name,
              role: user.role
            }));
            
            setAuthState({
              isLoading: false,
              isAuthenticated: true,
              userName: displayName,
              userRole: user.role || "viewer",
              user: user,
              sessionToken: authResult.session_token,
              initialized: true
            });
            
            // Schedule token refresh
            scheduleTokenRefresh(authResult.session_token);
            
            return true;
          } else {
            throw new Error("Failed to load user data after authentication");
          }
        } else {
          console.error("Auth: Authentication failed:", authResult.message);
          setAuthState(prev => ({ ...prev, isLoading: false }));
          throw new Error(authResult.message || "Invalid email or password");
        }
      } else {
        console.error("Auth: No data returned from authentication");
        setAuthState(prev => ({ ...prev, isLoading: false }));
        throw new Error("No response from authentication service");
      }
      
      return false;
    } catch (error: any) {
      console.error("Auth: Sign in error:", error.message);
      setAuthState(prev => ({ ...prev, isLoading: false }));
      throw error; // Re-throw to let the caller handle it
    }
  };

  // Enhanced helper function to get user by token with better error handling
  const getUserByToken = async (token: string): Promise<TeamMember | null> => {
    try {
      console.log("Auth: Validating token and getting user data");
      const { data, error } = await supabase.rpc('get_team_member_by_token', {
        p_token: token
      });
      
      if (error) {
        console.error("Auth: Error getting user by token:", error);
        
        // Check if it's a token expiry or invalid token error
        if (error.message?.includes('expired') || error.message?.includes('invalid') || error.code === 'PGRST116') {
          console.log("Auth: Token appears to be expired or invalid");
          handleExpiredToken();
        }
        return null;
      }
      
      if (!data || data.length === 0) {
        console.log("Auth: No user found for token - token may be expired");
        handleExpiredToken();
        return null;
      }
      
      console.log("Auth: Retrieved team member data:", data[0]);
      return data[0];
    } catch (error) {
      console.error("Auth: Error in getUserByToken:", error);
      // Assume token is invalid if we can't validate it
      handleExpiredToken();
      return null;
    }
  };

  // Enhanced load user by token with session health checks
  const loadUserByToken = async (token: string) => {
    try {
      console.log("Auth: Loading user by token with health check");
      
      // First check if token is healthy
      const isHealthy = await checkSessionHealth(token);
      if (!isHealthy) {
        console.log("Auth: Token failed health check");
        handleExpiredToken();
        return;
      }
      
      const user = await getUserByToken(token);
      
      if (user) {
        const displayName = user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : user.email.split('@')[0];
        
        console.log("Auth: User authenticated via token", { 
          id: user.id, 
          displayName, 
          email: user.email,
          role: user.role 
        });
        
        // Store user info in localStorage for consistency
        localStorage.setItem('auth_user', JSON.stringify({
          id: user.id,
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name,
          role: user.role
        }));
        
        setAuthState({
          isLoading: false,
          isAuthenticated: true,
          userName: displayName,
          userRole: user.role || "viewer",
          user: user,
          sessionToken: token,
          initialized: true
        });
        
        // Schedule token refresh for existing sessions
        scheduleTokenRefresh(token);
        return;
      }
      
      console.log("Auth: Invalid or expired token, clearing session");
      handleExpiredToken();
    } catch (error) {
      console.error("Auth: Error loading user by token:", error);
      handleExpiredToken();
    }
  };

  const signOut = async () => {
    try {
      setAuthState(prev => ({ ...prev, isLoading: true }));
      
      console.log("Auth: Signing out");
      
      // Clear refresh timeout
      if (window.authRefreshTimeout) {
        clearTimeout(window.authRefreshTimeout);
      }
      
      localStorage.removeItem('session_token');
      localStorage.removeItem('auth_user');
      
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userName: "User",
        userRole: "viewer",
        user: null,
        sessionToken: null,
        initialized: true
      });
      
      toast({
        title: "Logged out successfully",
        description: "You have been logged out of your account",
      });
    } catch (error: any) {
      console.error("Sign out error:", error.message);
      
      // Force logout even if there's an error
      localStorage.removeItem('session_token');
      localStorage.removeItem('auth_user');
      if (window.authRefreshTimeout) {
        clearTimeout(window.authRefreshTimeout);
      }
      
      setAuthState({
        isLoading: false,
        isAuthenticated: false,
        userName: "User",
        userRole: "viewer",
        user: null,
        sessionToken: null,
        initialized: true
      });
      
      toast({
        variant: "destructive",
        title: "Logout issue",
        description: "There was an error during logout, but you have been logged out",
      });
    }
  };

  // Force sign out without any async operations
  const forceSignOut = () => {
    console.log("Auth: Force signing out");
    
    if (window.authRefreshTimeout) {
      clearTimeout(window.authRefreshTimeout);
    }
    
    localStorage.removeItem('session_token');
    localStorage.removeItem('auth_user');
    setAuthState({
      isLoading: false,
      isAuthenticated: false,
      userName: "User",
      userRole: "viewer",
      user: null,
      sessionToken: null,
      initialized: true
    });
  };

  // Initialize auth state and storage settings on mount
  useEffect(() => {
    console.log("Auth: Starting enhanced auth initialization");
    
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('session_token');
        
        // Initialize storage settings
        await makeAvatarsBucketPublic();
        
        if (storedToken) {
          console.log("Auth: Found session token, validating with health check...");
          await loadUserByToken(storedToken);
        } else {
          console.log("Auth: No session token found");
          setAuthState(prev => ({
            ...prev,
            isLoading: false,
            initialized: true
          }));
        }
      } catch (error) {
        console.error("Auth initialization error:", error);
        handleExpiredToken();
      }
    };
    
    initializeAuth();

    // Cleanup refresh timeout on unmount
    return () => {
      if (window.authRefreshTimeout) {
        clearTimeout(window.authRefreshTimeout);
      }
    };
  }, []);

  // Add periodic session health checks (every 30 minutes)
  useEffect(() => {
    if (authState.isAuthenticated && authState.sessionToken) {
      const healthCheckInterval = setInterval(async () => {
        console.log("Auth: Performing periodic session health check");
        const isHealthy = await checkSessionHealth(authState.sessionToken!);
        if (!isHealthy) {
          console.log("Auth: Periodic health check failed, logging out");
          handleExpiredToken();
        }
      }, 1800000); // 30 minutes

      return () => clearInterval(healthCheckInterval);
    }
  }, [authState.isAuthenticated, authState.sessionToken]);

  const value = {
    isLoading: authState.isLoading,
    isAuthenticated: authState.isAuthenticated,
    userName: authState.userName,
    userRole: authState.userRole,
    user: authState.user,
    session: { user: authState.user }, // Add for compatibility
    signIn,
    signOut,
    forceSignOut
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Export the hook with proper error handling
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
